import styled from 'styled-components'
import { media } from '../../../utils/mediaQuery'

// Header components (matching ListingDetail pattern)
export const DetailHeaderInfo = styled.div`
  flex: 1;
  margin-right: ${({ theme }) => theme.spacing.medium};
`

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

// Back button for land detail sub-view
export const LandDetailBackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #00ade6;
  border-radius: 4px;
  transition: background 0.15s;

  &:hover {
    background: rgba(0, 173, 230, 0.08);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`

// Permit info section (moved from header to body)
export const PermitInfoSection = styled.div`
  padding: 16px 20px;
  background: linear-gradient(135deg, #00ade608 0%, #00ade603 100%);
  border-bottom: 1px solid #f0f0f0;
  container-type: inline-size;
`

export const PermitInfoMainContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 12px;
`

export const PermitInfoContent = styled.div`
  flex: 1;
`

export const DetailTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

export const DetailSubtitle = styled.div`
  font-size: 13px;
  color: #6b6b6b;
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  flex-wrap: wrap;
`

export const PermitMapContainer = styled.div`
  height: 140px;
  min-height: 140px;
  width: 300px;
  min-width: 300px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e5e5e5;
  position: relative;
  background: #f8f9fa;
  flex-shrink: 0;

  @container (max-width: 850px) {
    display: none;
  }

  .mapboxgl-popup-content {
    padding: 8px 10px;
    font-size: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .mapboxgl-ctrl-attrib {
    display: none;
  }
`

export const HeaderMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;

  ${media.tabletDown} {
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`

export const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const MetricLabel = styled.span`
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const MetricValue = styled.span<{ $highlight?: boolean }>`
  font-size: ${(props) => (props.$highlight ? '16px' : '14px')};
  font-weight: 600;
  color: ${(props) => (props.$highlight ? '#00ade6' : '#1a1a1a')};
`

// Content components
export const DetailContent = styled.div`
  padding: 16px 20px;
`

export const DataGroup = styled.div`
  background: white;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const DataSectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 6px;

  svg {
    width: 14px;
    height: 14px;
    opacity: 0.7;
  }
`

export const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  ${media.tabletDown} {
    grid-template-columns: 1fr;
  }
`

export const DataColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`

export const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f8f8f8;

  &:last-child {
    border-bottom: none;
  }
`

export const DataLabel = styled.span`
  font-size: 13px;
  color: #6b6b6b;
`

export const DataValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
  text-align: right;
`

export const DescriptionBox = styled.div`
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px;
  font-size: 12px;
  line-height: 1.5;
  color: #1a1a1a;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 300px;
  overflow-y: auto;
`

// Participant styles
export const ParticipantsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  ${media.tabletDown} {
    grid-template-columns: 1fr;
  }
`

export const ParticipantGroup = styled.div``

export const ParticipantGroupHeader = styled.div<{ $role?: string }>`
  padding: 6px 12px;
  background: ${({ $role }) => {
    switch ($role) {
      case 'owner':
        return 'rgba(0, 179, 65, 0.08)' // Green
      case 'architect':
        return 'rgba(0, 173, 230, 0.08)' // Blue
      case 'applicant':
        return 'rgba(255, 149, 0, 0.08)' // Orange
      default:
        return 'rgba(142, 142, 147, 0.08)' // Gray
    }
  }};
  border-radius: 6px 6px 0 0;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ $role }) => {
    switch ($role) {
      case 'owner':
        return '#00B341' // Green
      case 'architect':
        return '#00ADE6' // Blue
      case 'applicant':
        return '#FF9500' // Orange
      default:
        return '#8E8E93' // Gray
    }
  }};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

export const ParticipantList = styled.div`
  border: 1px solid #f0f0f0;
  border-top: none;
  border-radius: 0 0 6px 6px;
`

export const ParticipantCard = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid #f8f8f8;

  &:last-child {
    border-bottom: none;
  }
`

export const ParticipantType = styled.div`
  font-size: 10px;
  color: #888;
  margin-bottom: 2px;
`

export const ParticipantName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 4px;
`

export const ParticipantRole = styled.div`
  font-size: 11px;
  color: #6b6b6b;
  margin-bottom: 4px;
`

export const ParticipantDetails = styled.div`
  font-size: 11px;
  color: #6b6b6b;
  line-height: 1.4;

  > div {
    margin-bottom: 2px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`

// Card components (for lands and features)
export const PropertyCard = styled.div`
  background: white;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const PropertyHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f8f8f8;
`

export const PropertyHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const PropertyBadge = styled.span<{ $type?: string }>`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $type }) => {
    switch ($type) {
      case 'new construction':
        return 'rgba(0, 179, 65, 0.1)' // Green
      case 'transformation':
        return 'rgba(0, 173, 230, 0.1)' // Blue
      case 'demolition':
        return 'rgba(255, 59, 48, 0.1)' // Red
      default:
        return 'rgba(142, 142, 147, 0.1)'
    }
  }};
  color: ${({ $type }) => {
    switch ($type) {
      case 'new construction':
        return '#00B341' // Green
      case 'transformation':
        return '#00ADE6' // Blue
      case 'demolition':
        return '#FF3B30' // Red
      default:
        return '#8E8E93'
    }
  }};
`

export const MapMarkerBadge = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #2563eb;
  color: white;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
`

export const PropertyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const PropertyInfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;

  span:first-child {
    color: #888;
  }

  span:last-child {
    color: #1a1a1a;
    font-weight: 500;
  }
`

// Feature-specific
export const FeatureIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba(0, 173, 230, 0.1);
  color: #00ade6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  flex-shrink: 0;
`

export const FeatureContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export const FeatureTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 2px;
`

export const FeatureDetail = styled.div`
  font-size: 12px;
  color: #6b6b6b;
`

// RegBL flags list
export const FlagsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const FlagItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #1a1a1a;

  svg {
    width: 14px;
    height: 14px;
    color: #00b341;
    flex-shrink: 0;
  }
`

// Collapsible section
export const CollapsibleSection = styled.div<{ $isOpen: boolean }>`
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 12px;
`

export const CollapsibleHeader = styled.button`
  width: 100%;
  padding: 10px 12px;
  background: #fafafa;
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: #1a1a1a;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: #f5f5f5;
  }

  span {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  svg {
    width: 14px;
    height: 14px;
    color: #888;
    transition: transform 0.2s;
  }
`

export const CollapsibleContent = styled.div<{ $isOpen: boolean }>`
  display: ${(props) => (props.$isOpen ? 'block' : 'none')};
  padding: 12px;
  background: white;
`

// Loading and error states
export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 12px;
  height: 160px;
  color: #6b6b6b;
`

export const ErrorState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #ff3b30;

  h3 {
    font-size: 16px;
    margin-bottom: 8px;
  }

  p {
    font-size: 13px;
  }
`

// External link button
export const ExternalLinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #00ade6;
  color: white;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: #0099cc;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`

// Feature table (Scope of Work)
export const FeatureTable = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  overflow: hidden;
`

export const FeatureTableHeader = styled.div`
  display: grid;
  grid-template-columns:
    minmax(100px, 1.2fr) minmax(80px, 1fr) minmax(80px, 1fr)
    minmax(80px, 1fr);
  column-gap: 12px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-bottom: 1px solid #e5e5e5;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

export const FeatureTableRow = styled.div<{ $even?: boolean }>`
  display: grid;
  grid-template-columns:
    minmax(100px, 1.2fr) minmax(80px, 1fr) minmax(80px, 1fr)
    minmax(80px, 1fr);
  column-gap: 12px;
  padding: 8px 12px;
  background: ${({ $even }) => ($even ? '#fafafa' : 'white')};
  border-bottom: 1px solid #f0f0f0;
  font-size: 12px;
  color: #1a1a1a;
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
`

export const FeatureTableCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-transform: capitalize;
`

export const FeatureActionIcon = styled.div<{ $action?: string }>`
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  background: ${({ $action }) => {
    const a = $action?.toLowerCase()
    if (a === 'construction') return 'rgba(0, 179, 65, 0.1)'
    if (a === 'transformation' || a === 'renovation')
      return 'rgba(0, 173, 230, 0.1)'
    if (a === 'demolition') return 'rgba(255, 59, 48, 0.1)'
    return 'rgba(142, 142, 147, 0.1)'
  }};

  svg {
    width: 14px;
    height: 14px;
    color: ${({ $action }) => {
      const a = $action?.toLowerCase()
      if (a === 'construction') return '#00B341'
      if (a === 'transformation' || a === 'renovation') return '#00ADE6'
      if (a === 'demolition') return '#FF3B30'
      return '#8E8E93'
    }};
  }
`

// Land use list
export const LandUseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const LandUseItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #1a1a1a;

  &::before {
    content: '•';
    font-size: 16px;
    color: #00ade6;
  }
`

// Quick Summary Chips container
export const QuickSummaryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
`

// Clickable summary chip
export const SummaryChip = styled.button<{
  $variant?: 'success' | 'warning' | 'danger' | 'primary' | 'secondary' | 'info'
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  border: none;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  background: ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return 'rgba(0, 179, 65, 0.1)'
      case 'warning':
        return 'rgba(255, 149, 0, 0.1)'
      case 'danger':
        return 'rgba(255, 59, 48, 0.1)'
      case 'primary':
        return 'rgba(0, 173, 230, 0.1)'
      case 'info':
        return 'rgba(88, 86, 214, 0.1)'
      default:
        return 'rgba(142, 142, 147, 0.1)'
    }
  }};

  color: ${({ $variant }) => {
    switch ($variant) {
      case 'success':
        return '#00B341'
      case 'warning':
        return '#FF9500'
      case 'danger':
        return '#FF3B30'
      case 'primary':
        return '#00ADE6'
      case 'info':
        return '#5856D6'
      default:
        return '#6b6b6b'
    }
  }};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`
